import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import { Chip, MenuItem } from '@material-ui/core';
import { useField } from 'formik';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from '../../contexts/formContext';
import Tooltip from '../Tooltip';
import useFieldStyles from './Field.styles';

const formatSelectedValues = (selectedValues, options) =>
  selectedValues.reduce((accumulator, selectedValue) => {
    const option = options.find(({ value }) => (Array.isArray(value) ? value.includes(selectedValue) : value === selectedValue));

    if (!accumulator.map((v) => v.label).includes(option.label)) {
      accumulator.push({
        optionValue: option ? option.value : selectedValue,
        value: selectedValue,
        label: option ? option.label : selectedValue,
      });
    }

    return accumulator;
  }, []);

const MultipleSelectField = ({ className, name, label, options, placeholder, disabled, onChange, forceEnabled, muiClasses }) => {
  const fieldClasses = useFieldStyles();

  const [field, meta, helpers] = useField({ name });

  const { disabled: formDisabled } = useFormContext();

  const errorMessage = useMemo(() => (meta.touched && meta.error ? meta.error : ''), [meta.touched, meta.error]);

  const handleChange = useCallback(
    (event) => {
      const newEvent = event;
      newEvent.target.value = [].concat(...event.target.value);
      field.onChange(newEvent);
      if (onChange) onChange(newEvent.target.value);
    },
    [onChange, field]
  );

  const handleChipDelete = (value) => {
    const newValues = field.value.filter((e) => (Array.isArray(value) ? !value.includes(e) : value !== e));
    helpers.setValue(newValues);
    if (onChange) onChange(newValues);
  };

  return (
    <div className={className}>
      <Tooltip title={errorMessage} placement="bottom" classes={{ tooltip: fieldClasses.tooltip }}>
        <FormControl className={fieldClasses.root}>
          {!!label && <InputLabel id="select-label">{label}</InputLabel>}

          <Select
            classes={muiClasses}
            id={name}
            labelId="select-label"
            value={field.value === undefined || field.value === '' ? [] : field.value}
            onChange={handleChange}
            name={field.name}
            placeholder={placeholder}
            disabled={!forceEnabled && (formDisabled || disabled)}
            multiple
            error={!!(meta.touched && meta.error)}
            renderValue={(selectedValues) => (
              <div className={fieldClasses.chips}>
                {formatSelectedValues(selectedValues, options).map((value) => (
                  <Chip
                    key={value.value}
                    label={value.label}
                    onDelete={() => handleChipDelete(value.optionValue)}
                    className={fieldClasses.chip}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                  />
                ))}
              </div>
            )}>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Tooltip>
    </div>
  );
};

MultipleSelectField.propTypes = {
  disabled: PropTypes.bool,
  forceEnabled: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
      label: PropTypes.string,
    })
  ).isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  muiClasses: PropTypes.shape(),
};

MultipleSelectField.defaultProps = {
  disabled: false,
  forceEnabled: false,
  label: '',
  onChange: undefined,
  placeholder: '',
  className: undefined,
  muiClasses: undefined,
};

export default React.memo(MultipleSelectField);
